import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import type { Toast } from '../contexts/ToastContext';

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [entered, setEntered] = useState(false);

  // Trigger the enter transition on the next frame after mount.
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);

  // Gentle slide-in on enter; full slide-out to the right on dismiss. With reduced motion it only fades.
  const motion = toast.leaving
    ? 'translate-x-full opacity-0 motion-reduce:translate-x-0'
    : entered
      ? 'translate-x-0 opacity-100'
      : 'translate-x-6 opacity-0 motion-reduce:translate-x-0';

  const success = toast.type === 'success';
  const Icon = success ? CheckCircle2 : AlertCircle;
  const chip = success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700';

  return (
    // An error is announced at once (role="alert"); a confirmation waits for a pause (the region is polite).
    <div
      role={success ? undefined : 'alert'}
      className={`pointer-events-auto relative overflow-hidden rounded-xl bg-white ring-1 ring-gray-900/5 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.25)] transition-all duration-300 ease-out motion-reduce:transition-opacity ${motion}`}
    >
      <div className="flex items-start gap-3 p-3.5">
        <span aria-hidden className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${chip}`}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <p className="min-w-0 flex-1 pt-1 text-sm font-medium text-gray-900">
          <span className="sr-only">{success ? 'Done: ' : 'Problem: '}</span>
          {toast.message}
        </p>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

// The region is always in the page (empty when there is nothing to say): a screen reader only announces what is
// added to a live region that already exists. It sits above the side panels (z-index 1000002), so a message about
// something done in a panel is not hidden behind it.
export default function ToastContainer() {
  const { toasts, dismissToast } = useToast();

  return (
    <div
      data-testid="toasts"
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed right-4 top-4 z-[1000003] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2.5"
    >
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
